import React, { Component } from 'react';
import TaskProgressCard from 'components/TaskProgressCard';
import { bindActionCreators } from 'redux';
import {
  actions as taskActions,
  accessors as taskAccessors,
  taskStatisticActions,
} from 'data/Tasks';
import {
  ApiResponseHelper,
  connect,
  Grid,
  testClass,
  ConfirmDialog,
  PrivilegeChecker,
} from 'omni-common-ui';
import Privileges from 'domain/Privileges';
import {
  TableView,
  TableViewTr,
  StatusTd,
  DetailLinkTd,
  TextTd,
  ButtonTd,
} from 'components/TableView';
import PropTypes from 'prop-types';

import TaskFilter from './TaskFilter';

export class MyTasks extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filterParams: {
        pageSize: this.props.location.query.pageSize || 10,
        pageNumber: this.props.location.query.pageNumber || 1,
        sortBy: this.props.location.query.sortBy || 'generatedTime',
        sortType: this.props.location.query.sortType || 'Descending',
      },
      taskFilter: false,
      shouldPopupDialog: false,
    };
  }

  componentDidMount() {
    this._fetchTasks();
    this._fetchTaskStatistics();
  }

  componentWillUpdate(newProps, newState) {
    if (newState.filterParams === this.state.filterParams) {
      return;
    }

    this._fetchTasks(newState.filterParams);
    this._fetchTaskStatistics();
  }

  _onCancelTaskButtonClick(taskId) {
    this._toggleConfirmDialog(true, taskId);
  }

  _toggleConfirmDialog(isDialogShow, taskId) {
    this.setState({
      shouldPopupDialog: isDialogShow,
      currentTaskId: `${taskId}`,
    });
  }

  _onDiscardClicked() {
    this._toggleConfirmDialog(false);
  }

  _onSaveButtonClicked() {
    const currentTaskId = this.state.currentTaskId;
    if (currentTaskId) {
      this.props
        .cancelTask(currentTaskId)
        .then(() => this._toggleConfirmDialog(false))
        .then(() => this._fetchTasks(this.state.filterParams));
    }
  }

  _renderConfirmDialog() {
    return (
      <ConfirmDialog
        isOpen={this.state.shouldPopupDialog}
        primaryButtonContent="Yes"
        secondaryButtonContent="No"
        onPrimaryClick={() => this._onSaveButtonClicked()}
        onSecondaryClick={() => this._onDiscardClicked()}
        onRequestClose={() => this._onDiscardClicked()}
      >
        <span>This task will be deleted from task owner's task list.</span>
        <br />
        <span>Are you sure you want to cancel this task.</span>
      </ConfirmDialog>
    );
  }

  _renderStatisticCard() {
    const taskCounts = this.props.taskStatisticResult.taskStatistics || [];
    const sinceTime =
      this.props.taskStatisticResult.statisticFromDateTime || '';
    return (
      <TaskProgressCard
        cardTitle="My Tasks Completion"
        className={testClass('my-tasks-completion')}
        tasksCounts={taskCounts}
        sinceTime={sinceTime}
      />
    );
  }

  _handleOnFilterChange(filterParams) {
    this.setState({ filterParams });
  }

  _fetchTasks(
    filterParams = this.state.filterParams,
    taskFilter = this.state.taskFilter,
  ) {
    const filter = { ...filterParams, taskFilter };
    this.props.fetchTasks(filter);
  }

  _fetchTaskStatistics(taskFilter = this.state.taskFilter) {
    this.props.fetchTaskStatistics('my-tasks', taskFilter);
  }

  _handleOnTaskFilterChange(taskFilter) {
    this.setState({ taskFilter });
    this._fetchTasks(this.state.filterParams, taskFilter);
    this._fetchTaskStatistics(taskFilter);
  }

  _renderTableRow(row) {
    const { hasCancelTaskPrivilege } = this.props;

    return (
      <TableViewTr>
        <TextTd text={taskAccessors.getTaskSubject(row.subject)} />
        <StatusTd text={row.statusName} />
        <TextTd text={row.itemsAffected} />
        <TextTd text={row.details} />
        <TextTd text={row.dueDate} />
        {hasCancelTaskPrivilege && (
          <ButtonTd
            text="Cancel"
            onButtonClick={() => this._onCancelTaskButtonClick(row.id)}
          />
        )}
        <DetailLinkTd url={row.redirectUrl} />
      </TableViewTr>
    );
  }

  render() {
    const { taskFitlerResult, hasCancelTaskPrivilege } = this.props;

    if (!ApiResponseHelper.hasSucceeded(taskFitlerResult)) {
      return null;
    }

    const data = taskFitlerResult.data;

    const headers = [
      { key: 'subject', text: 'Subject', sortable: true },
      { key: 'statusName', text: 'Status', sortable: true },
      { key: 'itemsAffected', text: 'Items Affected', sortable: true },
      { key: 'description', text: 'Details', sortable: true },
      { key: 'dueDateTimeUtc', text: 'Due Date', sortable: true },
      { key: 'link', text: '', sortable: false },
    ];
    hasCancelTaskPrivilege &&
      headers.splice(-1, 0, { key: 'CancelButton', text: '', sortable: false });

    return (
      <Grid outerMargin>
        <Grid.Group>
          <Grid.Item>
            <TaskFilter
              value={this.state.taskFilter}
              handleFilterChange={(val) => this._handleOnTaskFilterChange(val)}
            />
          </Grid.Item>
          <Grid.Item>{this._renderStatisticCard()}</Grid.Item>
          <Grid.Item>
            <TableView
              tableTitle="My Tasks"
              className={testClass('my-tasks')}
              noResultsString="You have no tasks."
              totalRecords={data.totalRecords}
              source={data.tasks}
              headers={headers}
              pageable
              query={this.state.filterParams}
              renderTableRow={(row) => this._renderTableRow(row)}
              onFilterChange={(query) => this._handleOnFilterChange(query)}
            />
          </Grid.Item>
          {this._renderConfirmDialog()}
        </Grid.Group>
      </Grid>
    );
  }
}

MyTasks.propTypes = {
  taskFitlerResult: PropTypes.object,
  router: PropTypes.object,
  location: PropTypes.object,
  fetchTasks: PropTypes.func,
  taskStatisticResult: PropTypes.object,
  fetchTaskStatistics: PropTypes.func,
  hasCancelTaskPrivilege: PropTypes.bool,
  cancelTask: PropTypes.func,
};

function mapStateToProps(state) {
  const taskFitlerResult = state.get('rootReducer').get('taskFitlerResult');
  const taskStatisticResult = state
    .get('rootReducer')
    .get('taskStatisticResult');
  return {
    taskFitlerResult,
    taskStatisticResult,
    hasCancelTaskPrivilege: PrivilegeChecker.hasPrivilege(
      state,
      Privileges.GroupTask.CancelGroupTask,
    ),
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    {},
    bindActionCreators(taskActions, dispatch),
    bindActionCreators(taskStatisticActions, dispatch),
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(MyTasks);



// WEBPACK FOOTER //
// ./containers/UserDashboard/MyTasks/component.jsx